/**
 * React Query hooks for the owner self-service flow (sub-project ⑤B).
 *
 * The owner surface is gated by SIGN-IN, not role: any signed-in user reads their
 * dashboard, and the SERVER scopes it to their VERIFIED claims (empty for a
 * non-owner) — ownership is established by a verified claim, never by the
 * `developer` role (see `useSignedInOwnerGate`; do not re-add a role gate). The
 * dashboard is fetched ONCE and is the single source of truth for the
 * frontend-only edit-mode affordance: a project profile shows owner edit controls
 * iff its slug is in the owner's VERIFIED-claim set (`useOwnedSlugs`). This is a
 * pure UX convenience — the SERVER (⑤A `isOwner`) is the real authorization, so a
 * non-owner who forces a save still gets a 403 (surfaced as a typed error).
 *
 * Surfaces:
 *   • useOwnerDashboard()  — the developer's claimed projects (disabled while anon
 *     or non-developer; the key is namespaced per-uid so two users never share a
 *     cached dashboard, and a sign-out parks under a distinct, never-fetched key).
 *   • useOwnedSlugs()      — the derived set of verified-claim slugs + a `has(slug)`
 *     membership test for O(1) edit-mode detection on the profile.
 *   • useInitiateClaim / useVerifyClaim / useVerifyClaimEmail — claim-entry mutations.
 *   • useReleaseClaim                         — owner claim exit.
 *   • useEditOwnerField / useRevertOwnerField   — owner-edit mutations.
 *   • useRespondToConnect                       — accept/decline a Connect request.
 *
 * Every mutation invalidates the precise affected caches: claim/connect mutations
 * invalidate the dashboard; verified-claim transitions also invalidate the full
 * public raises namespace and every owner-scoped capital-stack read; field
 * edit/revert ALSO invalidate the project profile (`raisesKeys.profile(slug)`) so
 * the resolved value + provenance refreshes in place.
 *
 * Mirrors the codebase convention (`useConnect`, `useDealFlow`): a
 * `useQueryClient()` handle, stable serializable query keys, and no PII held in
 * any cache (the dashboard carries counts only; the connect outcome is non-PII).
 */

import { useMemo } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ownerApi,
  CapitalStackRateLimitError,
  CapitalStackUnsupportedError,
  OwnerBillingApiError,
  type ReleaseClaimReason,
} from '@/api/ownerApi';
import { useAuth } from '@/hooks/useAuth';
import { raisesKeys } from '@/hooks/queries/useRaises';
import { raisesOpsKeys } from '@/hooks/queries/raisesOpsKeys';
import type {
  ConnectDecisionOutcome,
  InitiateClaimInput,
  OwnerConnectAction,
  OwnerDashboardProject,
  OwnerEditValue,
  OwnerPendingRequest,
  ProjectClaim,
  ResolvedOwnerField,
  VerifyClaimInput,
} from '@/types/owner';
import type {
  CapitalStackRunDto,
  CapitalStackVisibilityDto,
  CandidateDossierDto,
  CandidateDossierPage,
  CandidateDossierDeal,
  CandidateDossierClaim,
} from '@/components/raises/capitalStack/types';
import type { CoverageBlock } from '@/components/raises/capitalStack/coverageTypes';

const MINUTE = 60 * 1000;

type CapitalStackScope = 'owner' | 'admin';

/** Prefix shared by every capital-stack query in one authorization scope. */
function capitalStackScopeKey(scope: CapitalStackScope) {
  return ['owner', 'capital-stack', scope] as const;
}

/**
 * Stable, serializable query keys. The dashboard key is namespaced by uid so two
 * users on the same device never share a cached dashboard, and a sign-out (uid →
 * null) parks under a distinct, never-fetched key.
 */
export const ownerKeys = {
  all: ['owner'] as const,
  dashboard: (uid: string | null) => ['owner', 'dashboard', uid] as const,
  pendingRequests: (uid: string | null) => ['owner', 'pending-requests', uid] as const,
  capitalStackScope: (scope: CapitalStackScope = 'owner') => capitalStackScopeKey(scope),
  /**
   * The latest capital-stack run for a raise, keyed by slug + scope. The scope
   * (`owner` vs `admin`) is part of the key because the two read paths return
   * DIFFERENT data for the same slug — the admin path includes an owner-hidden run,
   * the owner path filters it out — so they must never share a cache entry.
   */
  capitalStack: (slug: string | null, scope: CapitalStackScope = 'owner') =>
    [...capitalStackScopeKey(scope), slug] as const,
  /**
   * A per-candidate evidence dossier, keyed by scope + slug + entityId. Scope is part
   * of the key for the same owner-vs-admin reason as the run read (the admin path reads
   * an owner-hidden run's candidates). `null` entityId parks under a never-fetched key.
   */
  candidateDossier: (
    slug: string | null,
    entityId: number | null,
    scope: CapitalStackScope = 'owner',
  ) => [...capitalStackScopeKey(scope), slug, 'candidate', entityId] as const,
  /** A candidate's paginated deals or claims list (an infinite query over cursor pages). */
  candidateDossierList: (
    slug: string | null,
    entityId: number | null,
    kind: 'deals' | 'claims',
    scope: CapitalStackScope = 'owner',
  ) => [...capitalStackScopeKey(scope), slug, 'candidate', entityId, kind] as const,
};

/**
 * Refresh every read whose claim-sensitive projection can change when ownership is
 * verified. Pending claims affect only the owner's dashboard; verified claims also
 * retire the unclaimed coverage preview/badge from every viewer-scoped public raise
 * profile and directory cache under {@link raisesKeys.all}, refresh every owner-scoped
 * capital-stack result that a replay may have healed server-side, AND retire any
 * pre-claim empty `pendingRequests` cache — a project the account just verified may
 * already carry pending Connect requests, and a stale `[]` under that separately-keyed,
 * 1-minute-fresh query would otherwise leave the owner's actionable-requests section
 * empty until an unrelated refetch trigger (mirrors invite acceptance's behaviour).
 *
 * Awaiting the invalidations keeps mutation completion as the ordering barrier for
 * callers that navigate after `mutateAsync` resolves.
 */
async function invalidateClaimCaches(
  queryClient: QueryClient,
  uid: string | null,
  verified: boolean,
): Promise<void> {
  const invalidations = [
    queryClient.invalidateQueries({ queryKey: ownerKeys.dashboard(uid) }),
  ];
  if (verified) {
    invalidations.push(
      queryClient.invalidateQueries({ queryKey: raisesKeys.all }),
      queryClient.invalidateQueries({ queryKey: ownerKeys.capitalStackScope('owner') }),
      queryClient.invalidateQueries({ queryKey: ownerKeys.pendingRequests(uid) }),
    );
  }
  await Promise.all(invalidations);
}

/**
 * Owner-surface gate. Enabled for ANY signed-in user — ownership is established by
 * a VERIFIED claim (uid-based) and authorized SERVER-SIDE by `isOwner`, not by
 * role. The dashboard/edit surfaces must therefore work for whoever actually owns
 * a project (typically a `developer`, but also an `admin` or a role-transitioned
 * account holding a verified claim). The endpoint scopes every response to the
 * caller's own claims (empty for a non-owner), so enabling it for all signed-in
 * users is safe and avoids a role-gate that would hide an owner's own projects.
 * The cache key is per-uid so two users never share cached owner data.
 */
export function useSignedInOwnerGate(): { enabled: boolean; uid: string | null } {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  return { enabled: uid !== null, uid };
}

/**
 * The signed-in owner's dashboard: every claimed project with its claim status,
 * profile-completeness, pending Connect-request count, and interest analytics
 * (counts only — no PII). ENABLED for ANY signed-in user and DISABLED only while
 * signed out — ownership is established by a VERIFIED claim and authorized
 * server-side, NOT by role (do not re-add a role gate; it would hide an owner's
 * own projects — see {@link useSignedInOwnerGate}). The endpoint scopes every
 * response to the caller's own claims (empty for a non-owner). The cache key is
 * per-uid so a different user never reads a cached predecessor's dashboard.
 */
export function useOwnerDashboard() {
  const { enabled, uid } = useSignedInOwnerGate();
  return useQuery<OwnerDashboardProject[]>({
    queryKey: ownerKeys.dashboard(uid),
    queryFn: () => ownerApi.getOwnerDashboard(),
    enabled,
    staleTime: 2 * MINUTE,
    gcTime: 10 * MINUTE,
    // Round-8 LOW (owner-cache freshness): the app-level defaults disable window-focus and
    // interval refetching, and `staleTime` only MARKS data stale — it never initiates a fetch.
    // Without these per-query overrides a mounted, focused dashboard NEVER refetches, so an
    // admin-approved claim (pending → verified) never appears until a manual reload. Poll on
    // the staleTime cadence while mounted + refetch on focus (both scoped to this query only).
    refetchInterval: 2 * MINUTE,
    refetchOnWindowFocus: true,
  });
}

/**
 * The signed-in owner's PENDING Connect requests across all their claimed
 * projects (each carries the request id + the targeted project + the investor's
 * OWN consented facets/note — no PII). This is the data source for the owner
 * dashboard's `PendingRequestCard`s + the id the owner accepts/declines. ENABLED
 * for ANY signed-in user and DISABLED only while signed out — the server scopes
 * the response to the caller's verified-claim projects (empty for a non-owner),
 * so no role gate is applied here. The cache key is per-uid so a different user
 * never reads a cached predecessor's requests.
 */
export function useOwnerPendingRequests() {
  const { enabled, uid } = useSignedInOwnerGate();
  return useQuery<OwnerPendingRequest[]>({
    queryKey: ownerKeys.pendingRequests(uid),
    queryFn: () => ownerApi.getOwnerPendingRequests(),
    enabled,
    staleTime: 1 * MINUTE,
    gcTime: 10 * MINUTE,
    // Round-8 LOW (owner-cache freshness): without per-query overrides (global focus/interval
    // refetch are OFF), a mounted dashboard never sees a NEW investor Connect request arrive —
    // the pending list is the time-sensitive owner surface, so poll on the staleTime cadence
    // while mounted + refetch on focus.
    refetchInterval: 1 * MINUTE,
    refetchOnWindowFocus: true,
  });
}

/** The derived owned-slug set returned by {@link useOwnedSlugs}. */
export interface OwnedSlugs {
  /** The verified-claim, non-null slugs the viewer owns (stable, sorted by fetch order). */
  slugs: string[];
  /** O(1) membership test for edit-mode detection on a project profile. */
  has: (slug: string | null | undefined) => boolean;
  /** Whether the underlying dashboard query is still loading. */
  isLoading: boolean;
  /**
   * Whether the underlying dashboard query FAILED. Ownership is UNKNOWN in this
   * state — an empty membership set here is the ABSENCE of an answer, not proof the
   * viewer is a non-owner. Consumers must surface an ownership-unavailable state
   * rather than silently rendering investor/claim UI (round-7 #26); deriving "not
   * owner" from a 503 wrongly hides an owner's own controls and shows them the
   * public Connect + ClaimBanner on a raise they actually own.
   */
  isError: boolean;
  /** Re-run the underlying dashboard query — wired to the ownership-unknown retry. */
  refetch: () => void;
}

/**
 * The set of identifiers the viewer may edit — every VERIFIED claim
 * (pending/rejected claims confer NO ownership). Drives the frontend-only edit-mode
 * affordance on `RaiseProfile`. Returns an empty set for anon / non-developer
 * viewers (the dashboard is disabled).
 *
 * The `has()` membership set is keyed by BOTH the project's slug (when present) AND
 * its numeric id (as a string). A project still awaiting a backfilled slug is
 * therefore reachable + editable via `/raises/:id` — the `:slug` route param and
 * every owner endpoint accept an all-digit id as a slug-or-id fallback
 * (`projectIdForSlug`), so the id is the correct stable membership key. A slug is
 * always kebab-cased with a non-numeric token, so an id string can never collide
 * with a real slug. The `slugs` array remains the verified NON-NULL slugs only (its
 * documented shape); the id fallback lives purely in the membership test.
 */
export function useOwnedSlugs(): OwnedSlugs {
  const { data, isLoading, isError, refetch } = useOwnerDashboard();

  return useMemo<OwnedSlugs>(() => {
    const slugs: string[] = [];
    const set = new Set<string>();
    for (const row of data ?? []) {
      if (row.claim.status !== 'verified') continue;
      const { slug, id } = row.project;
      // Numeric-id fallback: recognize the owner whether the profile is reached by
      // slug (`/raises/:slug`) or by id (`/raises/:id`, e.g. before a slug backfill).
      if (typeof id === 'number' && Number.isFinite(id)) {
        set.add(String(id));
      }
      if (typeof slug === 'string' && slug.length > 0 && !set.has(slug)) {
        set.add(slug);
        slugs.push(slug);
      }
    }
    return {
      slugs,
      has: (slug) => (typeof slug === 'string' ? set.has(slug) : false),
      isLoading,
      isError,
      refetch: () => void refetch(),
    };
  }, [data, isLoading, isError, refetch]);
}

/** The verified-claim signal returned by {@link useHasVerifiedClaim}. */
export interface VerifiedClaimSignal {
  /** Whether the signed-in viewer holds at least one VERIFIED project claim. */
  hasVerifiedClaim: boolean;
  /** Whether the underlying dashboard query is still resolving. */
  isLoading: boolean;
}

/**
 * Whether the signed-in viewer owns AT LEAST ONE verified project claim — the
 * genuine "this account can reach the owner console" signal. Reuses the
 * sign-in-gated {@link useOwnerDashboard} (per-uid cache; server-scoped to the
 * caller's OWN claims, so a plain investor/developer with no claim gets an empty
 * array, never a 403 — safe to call for any signed-in user, NOT an owner-only
 * endpoint). Unlike {@link useOwnedSlugs}'s `slugs` array (verified NON-NULL slugs
 * only), this counts EVERY verified claim, including a project still awaiting a
 * slug backfill, so a dual-role owner is never missed. Returns `false` while
 * loading and for the signed-out state (the dashboard is disabled), so a
 * consumer that gates UI on it never shows an owner affordance speculatively.
 */
export function useHasVerifiedClaim(): VerifiedClaimSignal {
  const { data, isLoading } = useOwnerDashboard();
  const hasVerifiedClaim = useMemo(
    () => (data ?? []).some((row) => row.claim.status === 'verified'),
    [data],
  );
  return { hasVerifiedClaim, isLoading };
}

/**
 * Initiate a claim for a project (work-email domain auto-verify → `verified`, else
 * `pending` admin review). On success invalidates the dashboard so the new claim
 * (and any new owned slug) appears immediately; an immediately verified result also
 * retires the unclaimed projection from all public raise caches.
 */
export function useInitiateClaim() {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  return useMutation<ProjectClaim, Error, InitiateClaimInput>({
    mutationFn: ({ projectId, workEmail }) => ownerApi.initiateClaim(projectId, workEmail),
    onSuccess: async (claim) => {
      await invalidateClaimCaches(queryClient, uid, claim.status === 'verified');
    },
  });
}

/** Variables for releasing the signed-in owner's verified claim on a listing. */
export interface ReleaseClaimVariables {
  slug: string;
  reason: ReleaseClaimReason;
}

/** Reconcile every owner, admin, and public projection affected by a released claim. */
async function invalidateReleaseClaimCaches(
  queryClient: QueryClient,
  uid: string | null,
): Promise<void> {
  // wave-4 (#4): the acting user IS the released owner, so their OWN identity-scoped views can be
  // RESET — cleared AND refetched active under their own credentials. `resetQueries` (unlike a bare
  // `removeQueries`) forces an active observer to drop its stale data and refetch, so a mounted
  // dashboard / capital-stack / public list cannot keep rendering the retired claim or old amounts
  // (the installed TanStack leaves an active observer's last data in place after `removeQueries`).
  // Inactive cross-uid owner prefixes are defensively evicted (never refetched under this identity).
  queryClient.removeQueries({
    queryKey: ownerKeys.dashboard(null).slice(0, -1),
    type: 'inactive',
  });
  queryClient.removeQueries({
    queryKey: ownerKeys.pendingRequests(null).slice(0, -1),
    type: 'inactive',
  });

  await Promise.all([
    queryClient.resetQueries({ queryKey: ownerKeys.dashboard(uid) }),
    queryClient.resetQueries({ queryKey: ownerKeys.pendingRequests(uid) }),
    queryClient.resetQueries({ queryKey: ownerKeys.capitalStackScope('owner') }),
    queryClient.resetQueries({ queryKey: raisesKeys.all }),
    queryClient.invalidateQueries({ queryKey: raisesOpsKeys.claims }),
  ]);
}

/**
 * Release the caller's verified claim. Claim-sensitive cached projections are
 * evicted, while mounted identity-correct projections are refetched, before
 * mutation completion so a navigating caller cannot observe the retired claim or
 * stale public amounts.
 */
export function useReleaseClaim() {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  return useMutation<ProjectClaim, Error, ReleaseClaimVariables>({
    mutationFn: ({ slug, reason }) => ownerApi.releaseClaim(slug, reason),
    onSuccess: async () => {
      await invalidateReleaseClaimCaches(queryClient, uid);
      toast.success('Listing released');
    },
    onError: async (error) => {
      // The server may have committed the release before the response was lost.
      const message =
        error instanceof Error && error.message ? error.message : 'Could not release listing.';
      toast.error(message);
      // Reconcile every claim-sensitive projection after surfacing the failure so
      // a slow refetch cannot delay or suppress visible feedback.
      await invalidateReleaseClaimCaches(queryClient, uid);
    },
  });
}

/**
 * Complete a demand-triggered token claim (the ④A `?token=` link). On success
 * invalidates the dashboard and every public raise projection so the newly verified
 * claim appears immediately without a stale unclaimed coverage preview.
 */
export function useVerifyClaim() {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  return useMutation<ProjectClaim, Error, VerifyClaimInput>({
    mutationFn: ({ token, workEmail }) => ownerApi.verifyClaim(token, workEmail ?? null),
    onSuccess: async (claim) => {
      await invalidateClaimCaches(queryClient, uid, claim.status === 'verified');
    },
  });
}

/**
 * Complete the work-email verification round-trip (`POST /claims/verify-email`).
 * `token` is the single-use signed link emailed to the asserted work address; on
 * success the PENDING `email_verify` claim becomes `verified` (the server enforces
 * that the caller is the uid that initiated it). Invalidates the dashboard and every
 * public raise projection so the newly verified project appears immediately.
 */
export function useVerifyClaimEmail() {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  return useMutation<ProjectClaim, Error, { token: string }>({
    mutationFn: ({ token }) => ownerApi.verifyClaimEmail(token),
    onSuccess: async (claim) => {
      await invalidateClaimCaches(queryClient, uid, claim.status === 'verified');
    },
  });
}

/** Variables for an owner field edit: the project slug, the field key, the new value. */
export interface EditOwnerFieldVariables {
  slug: string;
  field: string;
  value: OwnerEditValue;
}

/**
 * Apply an authorized owner edit to a field. On success invalidates BOTH the
 * project profile (`raisesKeys.profile(slug)`) — so the resolved value + provenance
 * refresh in place and the ③B `ProvenanceTag` flips to "Updated by owner" — AND the
 * dashboard (the edit may change the project's completeness percentage).
 */
export function useEditOwnerField() {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  return useMutation<ResolvedOwnerField, Error, EditOwnerFieldVariables>({
    mutationFn: ({ slug, field, value }) => ownerApi.editOwnerField(slug, field, value),
    onSuccess: async (_data, { slug }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: raisesKeys.profile(slug) }),
        queryClient.invalidateQueries({ queryKey: ownerKeys.dashboard(uid) }),
        queryClient.invalidateQueries({ queryKey: raisesKeys.all }),
      ]);
    },
  });
}

/** Variables for an owner field revert: the project slug + the field key. */
export interface RevertOwnerFieldVariables {
  slug: string;
  field: string;
}

/**
 * Revert (remove) the owner override for a field, restoring the sourced/normalized
 * value. On success invalidates BOTH the project profile and the dashboard (the
 * revert may change the project's completeness percentage).
 */
export function useRevertOwnerField() {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  return useMutation<ResolvedOwnerField, Error, RevertOwnerFieldVariables>({
    mutationFn: ({ slug, field }) => ownerApi.revertOwnerField(slug, field),
    onSuccess: async (_data, { slug }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: raisesKeys.profile(slug) }),
        queryClient.invalidateQueries({ queryKey: ownerKeys.dashboard(uid) }),
        queryClient.invalidateQueries({ queryKey: raisesKeys.all }),
      ]);
    },
  });
}

/**
 * ADMIN god-mode edit of a raise's overlay field. Server-side this reuses the SAME ②
 * overlay primitive + registry validation as the owner path, authorized by `checkAdmin`
 * (no ownership check). Same variable/return shapes as {@link useEditOwnerField} so the
 * shared field-editor component can be driven by either. On success invalidates the
 * raise profile so the value + provenance refresh in place; there is no owner dashboard
 * to touch (the actor is an admin, not the owner).
 */
export function useAdminEditRaiseField() {
  const queryClient = useQueryClient();
  return useMutation<ResolvedOwnerField, Error, EditOwnerFieldVariables>({
    mutationFn: ({ slug, field, value }) => ownerApi.adminEditRaiseField(slug, field, value),
    onSuccess: async (_data, { slug }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: raisesKeys.profile(slug) }),
        queryClient.invalidateQueries({ queryKey: raisesKeys.all }),
      ]);
    },
  });
}

/** ADMIN revert of a raise's overlay field — admin-authorized twin of {@link useRevertOwnerField}. */
export function useAdminRevertRaiseField() {
  const queryClient = useQueryClient();
  return useMutation<ResolvedOwnerField, Error, RevertOwnerFieldVariables>({
    mutationFn: ({ slug, field }) => ownerApi.adminRevertRaiseField(slug, field),
    onSuccess: async (_data, { slug }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: raisesKeys.profile(slug) }),
        queryClient.invalidateQueries({ queryKey: raisesKeys.all }),
      ]);
    },
  });
}

/** Variables for an owner connect-decision: the request id + accept/decline. */
export interface RespondToConnectVariables {
  requestId: number;
  action: OwnerConnectAction;
}

/**
 * Accept or decline a Connect request as the verified owner. On success
 * invalidates BOTH the pending-request list (so the acted-on card leaves the
 * dashboard) AND the dashboard (so the pending-request badge + analytics refresh).
 * The result is the non-PII decision outcome (`id`/`status`/`ownerActionAt`).
 */
export function useRespondToConnect() {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  return useMutation<ConnectDecisionOutcome, Error, RespondToConnectVariables>({
    mutationFn: ({ requestId, action }) => ownerApi.respondToConnect(requestId, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ownerKeys.pendingRequests(uid) });
      queryClient.invalidateQueries({ queryKey: ownerKeys.dashboard(uid) });
    },
  });
}

// ── Capital-stack bridge (B2) ──────────────────────────────────────────────────

/**
 * Translates a generate-mutation failure into a single friendly toast, keyed by the
 * typed error class so each failure mode reads clearly:
 *   • 429 rate-limit           → a transient "try again later" notice.
 *   • 422 unsupported listing   → the server's actionable message (missing facet).
 *   • 402 subscription lapsed   → NO toast; the container renders the inline paywall
 *     from the actual 402 payload (trial-end date + code). A toast here would be a
 *     lossy duplicate that discards the payload (#11).
 *   • anything else             → the error's message (403/404/etc.) or a fallback.
 * The panel ALSO renders the 422 unsupported state inline (the toast is the
 * ephemeral confirmation of the failed action); the two are complementary.
 */
function toastCapitalStackError(error: unknown): void {
  if (error instanceof CapitalStackRateLimitError) {
    toast.error(error.message);
    return;
  }
  if (error instanceof CapitalStackUnsupportedError) {
    toast.error(error.message);
    return;
  }
  // A 402 is surfaced as an INLINE paywall by the container (OwnerPaywallNotice from the
  // real payload), not a toast — leave it for the caller's onError to handle (#11).
  if (error instanceof OwnerBillingApiError) {
    return;
  }
  const message =
    error instanceof Error && error.message ? error.message : 'Could not generate the capital stack.';
  toast.error(message);
}

/**
 * The owner's LATEST VISIBLE capital-stack run for a raise (or `null` when none has
 * been generated / all are owner-hidden). Enabled only when a slug is present AND the
 * caller opts in (`enabled`) — the panel enables it for the owner and for the entitled
 * read-only viewer, never speculatively. The verified-owner gate is server-side; a
 * non-owner who somehow enables it gets a 403 surfaced as a query error, never data.
 */
export function useCapitalStack(
  slug: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = (options.enabled ?? true) && typeof slug === 'string' && slug.length > 0;
  return useQuery<CapitalStackRunDto | null>({
    queryKey: ownerKeys.capitalStack(slug ?? null, 'owner'),
    queryFn: () => ownerApi.getCapitalStack(slug as string),
    enabled,
    staleTime: 2 * MINUTE,
    gcTime: 10 * MINUTE,
  });
}

/**
 * Generate a fresh capital-stack run for the owner's raise. On success invalidates
 * the owner-scoped capital-stack query so the new run renders in place, AND the
 * owner dashboard — whose per-project summary (map-generated? coverage counts?) is
 * derived from the latest run and stays fresh for two minutes, so without this an
 * owner who generates a run from the profile and returns to `/owner` within that
 * window still sees the stale "No capital map generated yet" summary. A 429
 * (rate-limit) or 422 (unsupported listing) is surfaced as a friendly toast; the
 * component may ALSO branch on the thrown typed error for its inline state.
 */
export function useGenerateCapitalStack(slug: string | null | undefined) {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  return useMutation<CapitalStackRunDto, Error, void>({
    mutationFn: () => ownerApi.generateCapitalStack(slug as string),
    onSuccess: async (run) => {
      queryClient.setQueryData(ownerKeys.capitalStack(slug ?? null, 'owner'), run);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ownerKeys.capitalStack(slug ?? null, 'owner') }),
        queryClient.invalidateQueries({ queryKey: ownerKeys.dashboard(uid) }),
      ]);
    },
    onError: toastCapitalStackError,
  });
}

/**
 * Owner-controlled project publication. Publishing targets one run; unpublishing
 * uses the route run id only as a compatibility pointer and clears the project's
 * live publication server-side.
 */
export function useSetCapitalStackPublication(slug: string | null | undefined) {
  const queryClient = useQueryClient();
  const { uid } = useSignedInOwnerGate();
  return useMutation<
    CapitalStackVisibilityDto,
    Error,
    { runId: number; visibleToInvestors: boolean }
  >({
    mutationFn: ({ runId, visibleToInvestors }) =>
      ownerApi.setCapitalStackPublication(
        slug as string,
        runId,
        visibleToInvestors,
      ),
    onSuccess: async (visibility) => {
      queryClient.setQueryData<CapitalStackRunDto | null>(
        ownerKeys.capitalStack(slug ?? null, 'owner'),
        (current) => {
          if (!current) return current;
          const projectPublication = {
            currentlyPublishedRunId: visibility.currentlyPublishedRunId,
            currentlyPublishedGeneratedAt: visibility.currentlyPublishedGeneratedAt,
          };
          // The current preview can be newer than the run being revoked. In that
          // case only project-level publication metadata changes; never overwrite
          // the preview's own visibility/run identity with the older run's echo.
          return current.runId === visibility.runId
            ? { ...current, ...visibility }
            : { ...current, ...projectPublication };
        },
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ownerKeys.capitalStack(slug ?? null, 'owner'),
        }),
        // Publication changes the public profile headline and directory coverage
        // badge for every viewer scope. The broad namespace intentionally replaces
        // the narrower capital-stack-only invalidation.
        queryClient.invalidateQueries({ queryKey: raisesKeys.all }),
        queryClient.invalidateQueries({ queryKey: ownerKeys.dashboard(uid) }),
      ]);
      toast.success(
        visibility.visibleToInvestors
          ? 'Capital stack published to entitled investors.'
          : 'Capital stack unpublished from investors.',
      );
    },
    onError: (error) => {
      // A gated 402 (publish rejected — subscription lapsed) is surfaced as an INLINE
      // publish paywall by the container's per-call onError (OwnerPaywallNotice
      // variant="publish"), NOT a toast — a toast here would be a lossy duplicate that
      // discards the 402 payload and reads as a generic failure (round-7 #23, mirroring
      // the generate-path #11 treatment). Leave it for the caller to render.
      if (error instanceof OwnerBillingApiError && error.status === 402) {
        return;
      }
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Could not update investor publication.';
      toast.error(message);
    },
  });
}

/**
 * ADMIN read of the latest run for a raise INCLUDING an owner-hidden one (curation
 * surface). Admin-scoped cache key so it never collides with the owner read.
 */
export function useAdminCapitalStack(
  slug: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const enabled = (options.enabled ?? true) && typeof slug === 'string' && slug.length > 0;
  return useQuery<CapitalStackRunDto | null>({
    queryKey: ownerKeys.capitalStack(slug ?? null, 'admin'),
    queryFn: () => ownerApi.adminGetCapitalStack(slug as string),
    enabled,
    staleTime: 2 * MINUTE,
    gcTime: 10 * MINUTE,
  });
}

/** The persona that owns a candidate-dossier read — mirrors the capital-stack scope. */
export type CandidateDossierScope = CapitalStackScope;

/**
 * W3: the per-candidate evidence dossier for one entity in a raise's latest run. The
 * `scope` picks the owner (verified-ownership gated, visible run) vs. admin (checkAdmin,
 * includes owner-hidden) read path — the server IDOR-fences the entity to the raise's own
 * run either way. Disabled until an entityId is provided (the drawer passes `null` when
 * closed). A 404 (entity not in the run) surfaces as `isError` for the drawer's honest
 * error state; it is deliberately NOT retried (a fenced-out id will not become valid).
 */
export function useCandidateDossier(
  slug: string | null | undefined,
  entityId: number | null,
  scope: CandidateDossierScope = 'owner',
  options: { enabled?: boolean } = {},
) {
  const enabled =
    (options.enabled ?? true) &&
    typeof slug === 'string' &&
    slug.length > 0 &&
    typeof entityId === 'number';
  return useQuery<CandidateDossierDto>({
    queryKey: ownerKeys.candidateDossier(slug ?? null, entityId, scope),
    queryFn: () =>
      scope === 'admin'
        ? ownerApi.adminGetCandidateDossier(slug as string, entityId as number)
        : ownerApi.getCandidateDossier(slug as string, entityId as number),
    enabled,
    retry: false,
    staleTime: 2 * MINUTE,
    gcTime: 10 * MINUTE,
  });
}

/** Cursor for the candidate list infinite queries — `undefined` fetches the first page. */
type DossierCursor = number | undefined;

/** Derive the next cursor from a page, or `undefined` to stop (no more pages). */
function nextDossierCursor<T>(page: CandidateDossierPage<T>): DossierCursor {
  return page.pagination.hasMore ? page.pagination.nextAfter ?? undefined : undefined;
}

/**
 * W3: the candidate's portfolio deals as an INFINITE query (cursor pagination) — the
 * Deals tab flattens `data.pages` and drives "Load more" via `fetchNextPage`. Same scope
 * split + server IDOR fence as {@link useCandidateDossier}; every page re-runs the fence.
 */
export function useCandidateDeals(
  slug: string | null | undefined,
  entityId: number | null,
  scope: CandidateDossierScope = 'owner',
  options: { enabled?: boolean } = {},
) {
  const enabled =
    (options.enabled ?? true) &&
    typeof slug === 'string' &&
    slug.length > 0 &&
    typeof entityId === 'number';
  return useInfiniteQuery({
    queryKey: ownerKeys.candidateDossierList(slug ?? null, entityId, 'deals', scope),
    queryFn: ({ pageParam }: { pageParam: DossierCursor }) =>
      scope === 'admin'
        ? ownerApi.adminGetCandidateDeals(slug as string, entityId as number, { after: pageParam })
        : ownerApi.getCandidateDeals(slug as string, entityId as number, { after: pageParam }),
    initialPageParam: undefined as DossierCursor,
    getNextPageParam: (last: CandidateDossierPage<CandidateDossierDeal>) => nextDossierCursor(last),
    enabled,
    retry: false,
    staleTime: 2 * MINUTE,
    gcTime: 10 * MINUTE,
  });
}

/**
 * W3: the candidate's claim evidence as an INFINITE query (cursor pagination) — the
 * Evidence tab flattens `data.pages`, groups by field, and drives "Load more" via
 * `fetchNextPage`. Same scope split + per-page IDOR fence.
 */
export function useCandidateClaims(
  slug: string | null | undefined,
  entityId: number | null,
  scope: CandidateDossierScope = 'owner',
  options: { enabled?: boolean } = {},
) {
  const enabled =
    (options.enabled ?? true) &&
    typeof slug === 'string' &&
    slug.length > 0 &&
    typeof entityId === 'number';
  return useInfiniteQuery({
    queryKey: ownerKeys.candidateDossierList(slug ?? null, entityId, 'claims', scope),
    queryFn: ({ pageParam }: { pageParam: DossierCursor }) =>
      scope === 'admin'
        ? ownerApi.adminGetCandidateClaims(slug as string, entityId as number, { after: pageParam })
        : ownerApi.getCandidateClaims(slug as string, entityId as number, { after: pageParam }),
    initialPageParam: undefined as DossierCursor,
    getNextPageParam: (last: CandidateDossierPage<CandidateDossierClaim>) => nextDossierCursor(last),
    enabled,
    retry: false,
    staleTime: 2 * MINUTE,
    gcTime: 10 * MINUTE,
  });
}

/**
 * ADMIN generate for any raise. Invalidates BOTH scopes' caches (the admin curation
 * view AND the owner view, which will now see the fresh run if it is visible).
 */
export function useAdminGenerateCapitalStack(slug: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<CapitalStackRunDto, Error, void>({
    mutationFn: () => ownerApi.adminGenerateCapitalStack(slug as string),
    onSuccess: (run) => {
      queryClient.setQueryData(ownerKeys.capitalStack(slug ?? null, 'admin'), run);
      queryClient.invalidateQueries({ queryKey: ownerKeys.capitalStack(slug ?? null, 'admin') });
      queryClient.invalidateQueries({ queryKey: ownerKeys.capitalStack(slug ?? null, 'owner') });
    },
    onError: toastCapitalStackError,
  });
}

/** Variables for an admin visibility toggle: the run id + the new visibility. */
export interface SetCapitalStackVisibilityVariables {
  runId: number;
  visibleToOwner?: boolean;
  visibleToInvestors?: boolean;
}

/**
 * ADMIN curation: toggle whether a run is visible to the owner or investors. On
 * success invalidates BOTH private scopes (the admin view refreshes the flag; the
 * owner view now sees or loses the run). Investor-publication transitions refresh
 * the full public raises namespace; owner-only curation keeps the narrower public
 * capital-stack invalidation. Surfaces a confirmation / failure toast.
 */
export function useAdminSetCapitalStackVisibility(slug: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<
    CapitalStackVisibilityDto,
    Error,
    SetCapitalStackVisibilityVariables
  >({
    mutationFn: ({ runId, ...visibility }) =>
      ownerApi.adminSetCapitalStackVisibility(slug as string, runId, visibility),
    onSuccess: async (result, variables) => {
      queryClient.setQueryData<CapitalStackRunDto | null>(
        ownerKeys.capitalStack(slug ?? null, 'admin'),
        (current) => (current?.runId === result.runId ? { ...current, ...result } : current),
      );
      const publicQueryKey =
        typeof variables.visibleToInvestors === 'boolean'
          ? raisesKeys.all
          : raisesKeys.capitalStack(slug ?? null);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ownerKeys.capitalStack(slug ?? null, 'admin'),
        }),
        queryClient.invalidateQueries({
          queryKey: ownerKeys.capitalStack(slug ?? null, 'owner'),
        }),
        queryClient.invalidateQueries({ queryKey: publicQueryKey }),
      ]);
      toast.success('Capital-stack visibility updated.');
    },
    onError: (error) => {
      const message =
        error instanceof Error && error.message ? error.message : 'Could not update visibility.';
      toast.error(message);
    },
  });
}

/**
 * ADMIN coverage recompute (W9): re-derives a run's coverage block server-side (the
 * §2.4 predicate/version-bump safety valve — mutates ONLY `coverage`, never
 * stack/tiers). On success invalidates BOTH private scopes so the admin panel and a
 * visible owner view refetch the run with the fresh block. Toast on both outcomes.
 */
export function useAdminRecomputeCapitalStackCoverage(slug: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<CoverageBlock, Error, { runId: number }>({
    mutationFn: ({ runId }) =>
      ownerApi.adminRecomputeCapitalStackCoverage(slug as string, runId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ownerKeys.capitalStack(slug ?? null, 'admin'),
        }),
        queryClient.invalidateQueries({
          queryKey: ownerKeys.capitalStack(slug ?? null, 'owner'),
        }),
      ]);
      toast.success('Coverage recomputed.');
    },
    onError: (error) => {
      const message =
        error instanceof Error && error.message ? error.message : 'Could not recompute coverage.';
      toast.error(message);
    },
  });
}
